import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { QrCode, Plus, Download, Upload, Image as ImageIcon, GripVertical, Trash2, ImagePlus, Edit, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/photobooth`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("circle");
  const [autoPrint, setAutoPrint] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateImages, setTemplateImages] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [showImageView, setShowImageView] = useState(false);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [ownerImageFile, setOwnerImageFile] = useState(null);
  const [uploadingOwnerImage, setUploadingOwnerImage] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, getAuthHeaders());
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates:", error);
      toast.error("Failed to load templates");
    }
  };

  const createTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    try {
      await axios.post(`${API}/templates`, { 
        name: newTemplateName,
        template_type: templateType,
        auto_print: autoPrint
      }, getAuthHeaders());
      toast.success("Template created successfully");
      setNewTemplateName("");
      setTemplateType("circle");
      setAutoPrint(false);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to create template:", error);
      toast.error(error.response?.data?.detail || "Failed to create template");
    }
  };

  const uploadBackground = async (templateId) => {
    if (!backgroundFile) {
      toast.error("Please select a background image");
      return;
    }

    const formData = new FormData();
    formData.append("file", backgroundFile);

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/templates/${templateId}/background`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
      });
      toast.success("Background uploaded successfully");
      setBackgroundFile(null);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to upload background:", error);
      toast.error("Failed to upload background");
    }
  };

  const viewQRCode = (templateId) => {
    setQrCodeUrl(`${API}/templates/${templateId}/qr`);
    setShowQR(true);
  };

  const viewImages = async (template) => {
    try {
      const response = await axios.get(`${API}/templates/${template.id}/images`, getAuthHeaders());
      setTemplateImages(response.data);
      setSelectedTemplate(template);
      setShowImageView(true);
    } catch (error) {
      console.error("Failed to load images:", error);
      toast.error("Failed to load images");
    }
  };

  const downloadPDF = async (templateId, templateName) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/templates/${templateId}/pdf`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${templateName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const moveImage = async (imageId, newIndex) => {
    try {
      await axios.put(`${API}/templates/${selectedTemplate.id}/move-image`, {
        image_id: imageId,
        new_circle_index: newIndex,
      });
      toast.success("Image position updated");
      viewImages(selectedTemplate);
    } catch (error) {
      toast.error("Failed to move image");
    }
  };

  const uploadOwnerImage = async (templateId) => {
    if (!ownerImageFile) {
      toast.error("Please select an image");
      return;
    }

    setUploadingOwnerImage(true);
    const formData = new FormData();
    formData.append("file", ownerImageFile);

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/templates/${templateId}/upload-owner`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
      });
      toast.success("Image uploaded successfully");
      setOwnerImageFile(null);
      fetchTemplates();
      if (selectedTemplate && selectedTemplate.id === templateId) {
        viewImages(selectedTemplate);
      }
    } catch (error) {
      if (error.response?.status === 507) {
        toast.error("Out of memory. Please try with a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to upload image");
      }
    } finally {
      setUploadingOwnerImage(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    try {
      await axios.delete(`${API}/images/${imageId}`, getAuthHeaders());
      toast.success("Image deleted successfully");
      fetchTemplates();
      if (selectedTemplate) {
        viewImages(selectedTemplate);
      }
    } catch (error) {
      console.error("Failed to delete image:", error);
      toast.error("Failed to delete image");
    }
  };

  const printTemplate = async (templateId, templateName) => {
    try {
      // Try to print
      const response = await axios.post(`${API}/templates/${templateId}/print`, {}, getAuthHeaders());
      toast.success("Print job sent to printer");
    } catch (error) {
      // If printing fails, download PDF instead
      if (error.response?.status === 500) {
        toast.error("No printer configured. Downloading PDF instead...");
        downloadPDF(templateId, templateName);
      } else {
        console.error("Print failed:", error);
        toast.error(error.response?.data?.detail || "Print failed");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="mb-12">
          <h1 className="text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            QR Circle Templates
          </h1>
          <p className="text-lg text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>Create, manage, and print your event photo templates</p>
        </div>

        <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90" data-testid="create-template-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <Plus className="w-6 h-6" />
              Create New Template
            </CardTitle>
            <CardDescription>Start a new template for your event or party</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Input
                  data-testid="template-name-input"
                  placeholder="Enter template name (e.g., Birthday Party 2025)"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && createTemplate()}
                  className="flex-1"
                />
                <Button 
                  data-testid="create-template-btn"
                  onClick={createTemplate}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  Create
                </Button>
              </div>
              
              <div className="flex gap-3">
                <button
                  data-testid="circle-template-type-btn"
                  onClick={() => setTemplateType("circle")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "circle" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">⭕</div>
                    <div className="font-semibold">Circle Template</div>
                    <div className="text-xs text-gray-500 mt-1">1.97" circles • ~20 per page</div>
                  </div>
                </button>
                
                <button
                  data-testid="rectangle-template-type-btn"
                  onClick={() => setTemplateType("rectangle")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "rectangle" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">▭</div>
                    <div className="font-semibold">Rectangle Template</div>
                    <div className="text-xs text-gray-500 mt-1">3.5"×5.5" boxes • 4 per page</div>
                  </div>
                </button>
                
                <button
                  data-testid="single-template-type-btn"
                  onClick={() => setTemplateType("single")}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${ 
                    templateType === "single" 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-gray-200 bg-white hover:border-purple-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📄</div>
                    <div className="font-semibold">Single Photo</div>
                    <div className="text-xs text-gray-500 mt-1">3.5"×5.5" rectangle • 1 photo • Auto-print</div>
                  </div>
                </button>
              </div>
              
              {templateType === "single" && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="auto-print"
                    checked={autoPrint}
                    onChange={(e) => setAutoPrint(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="auto-print-checkbox"
                  />
                  <label htmlFor="auto-print" className="text-sm font-medium text-gray-700">
                    Auto-print immediately when photo uploaded
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="border-0 shadow-xl backdrop-blur-sm bg-white/90 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <div className="flex items-center gap-2">
                    {template.template_type === "rectangle" ? "▭" : template.template_type === "single" ? "📄" : "⭕"}
                    {template.name}
                  </div>
                </CardTitle>
                <CardDescription>
                  {template.template_type === "single" 
                    ? `Single 3.5"×5.5" photo • ${template.filled_count}/1 filled`
                    : `${template.filled_count} / ${template.total_slots} ${template.template_type === "rectangle" ? "rectangles" : "circles"} filled`
                  }
                  {template.auto_print && " • Auto-print ON"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-bold text-purple-600">
                      {Math.round((template.filled_count / template.total_slots) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${(template.filled_count / template.total_slots) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      data-testid={`upload-bg-btn-${template.id}`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Background
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="background-upload-dialog">
                    <DialogHeader>
                      <DialogTitle>Upload Background Image</DialogTitle>
                      <DialogDescription>
                        This background will be applied to all photos in this template
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="background-upload">Select Image</Label>
                        <Input
                          id="background-upload"
                          type="file"
                          accept="image/*"
                          data-testid="background-file-input"
                          onChange={(e) => setBackgroundFile(e.target.files[0])}
                          className="mt-2"
                        />
                      </div>
                      <Button
                        data-testid="confirm-upload-bg-btn"
                        onClick={() => uploadBackground(template.id)}
                        className="w-full"
                      >
                        Upload
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid={`view-qr-btn-${template.id}`}
                  onClick={() => viewQRCode(template.id)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  View QR Code
                </Button>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid={`add-image-btn-${template.id}`}
                    >
                      <ImagePlus className="w-4 h-4 mr-2" />
                      Add Your Image
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="owner-image-upload-dialog">
                    <DialogHeader>
                      <DialogTitle>Upload Image</DialogTitle>
                      <DialogDescription>
                        Add an image directly to this template
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="owner-image-upload">Select Image</Label>
                        <Input
                          id="owner-image-upload"
                          type="file"
                          accept="image/*"
                          data-testid="owner-image-file-input"
                          onChange={(e) => setOwnerImageFile(e.target.files[0])}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Keep under 5MB for best results
                        </p>
                      </div>
                      <Button
                        data-testid="confirm-upload-owner-image-btn"
                        onClick={() => uploadOwnerImage(template.id)}
                        className="w-full"
                        disabled={uploadingOwnerImage || !ownerImageFile}
                      >
                        {uploadingOwnerImage ? "Processing..." : "Upload"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid={`edit-template-btn-${template.id}`}
                  onClick={() => navigate(`/editor/${template.id}`)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Open Editor
                </Button>

                <Button
                  data-testid={`view-images-btn-${template.id}`}
                  onClick={() => viewImages(template)}
                  variant="outline"
                  className="w-full"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  View Images ({template.filled_count})
                </Button>

                <Button
                  data-testid={`print-btn-${template.id}`}
                  onClick={() => printTemplate(template.id, template.name)}
                  variant="outline"
                  className="w-full"
                  disabled={template.filled_count === 0}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Now
                </Button>

                <Button
                  data-testid={`download-pdf-btn-${template.id}`}
                  onClick={() => downloadPDF(template.id, template.name)}
                  variant="outline"
                  className="w-full"
                  disabled={template.filled_count === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={showQR} onOpenChange={setShowQR}>
          <DialogContent data-testid="qr-code-dialog">
            <DialogHeader>
              <DialogTitle>QR Code for Upload</DialogTitle>
              <DialogDescription>
                Share this QR code with guests to upload their photos
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center p-6">
              {qrCodeUrl && (
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code" 
                  data-testid="qr-code-image"
                  className="w-64 h-64 border-4 border-gray-200 rounded-lg shadow-lg" 
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showImageView} onOpenChange={setShowImageView}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="images-view-dialog">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name} - Images</DialogTitle>
              <DialogDescription>
                Drag images to rearrange their positions
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-4 p-4">
              {templateImages.map((img) => (
                <div
                  key={img.id}
                  className="relative group"
                  data-testid={`image-item-${img.id}`}
                >
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-purple-500 transition-all">
                    <img
                      src={`${API}/images/${img.id}`}
                      alt={`Circle ${img.circle_index}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    #{img.circle_index + 1}
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => deleteImage(img.id)}
                      data-testid={`delete-image-btn-${img.id}`}
                      className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                      title="Delete image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <Input
                      type="number"
                      min="0"
                      max={selectedTemplate?.total_slots - 1}
                      value={img.circle_index}
                      data-testid={`position-input-${img.id}`}
                      onChange={(e) => {
                        const newIndex = parseInt(e.target.value);
                        if (!isNaN(newIndex)) {
                          moveImage(img.id, newIndex);
                        }
                      }}
                      className="text-xs text-center"
                      placeholder="Position"
                    />
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}